import React, { useContext, useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  doc,
  writeBatch,
  orderBy,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { toast } from "react-toastify";
import { db, storage, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import {
  getClientEditMeta,
  writeFullAuditLog,
  sliderSnapshot,
  websiteSnapshot,
} from "../utils/editAudit";
import {
  ADMIN_CONTENT_DOC_ID,
  contentFieldsOnly,
} from "../constants/adminContent";
import { loadOrMigrateAdminContent } from "../utils/adminContentStore";
const DataContext = React.createContext();
export const useData = () => {
  return useContext(DataContext);
};

const DataProvider = ({ children }) => {
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [postsData, setPostsData] = useState([]);
  const [categoriesData, setCategoriesData] = useState(null);
  const [websiteContentData, setWebsiteContentData] = useState(null);
  const [logo, setLogo] = useState(null);
  const [slidersData, setSlidersData] = useState([]);

  //---------------------Fetching all data at start---------------------------

  useEffect(() => {
    fetchLogo();
    setTableLoading(false);
    fetchUsers();
    fetchPosts();
    fetchCategories();
    fetchWebsiteContent();
    fetchSliders();
  }, []);

  //-------------------------------Logo---------------------------------------
  const fetchLogo = async () => {
    const storageRef = ref(storage, `assets/logo.png`);
    const downloadURL = await getDownloadURL(storageRef);
    setLogo(downloadURL);
  };

  // --------------------------Users related----------------------------------
  //Fetch users data from firebase
  const formatDate = (timestamp) => {
    const date = new Date(timestamp.seconds * 1000); // Convert Firestore timestamp to JavaScript Date object
    return date.toLocaleDateString(); // Adjust the date format as needed
  };

  const fetchUsers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "users"), query("registration_time" , "desc"));
      const temp = querySnapshot.docs.sort((a, b) => {
        return b.data().registration_time - a.data().registration_time;
      }).map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          email: data.email,
          registration_time: data.registration_time ? formatDate(data.registration_time) : "N/A",
        };
      });
      setTableData(temp);
    } catch (e) {
      console.log(e);
    }
  };

  // Fetch user to update user name in the firebase
  const fetchAndUpdateName = async (email, updatedUsername) => {
    setDeleteLoading(true);
    try {
      // Get the reference to the "users" collection
      const usersRef = collection(db, "users");

      // Create a query to find the document with the matching email
      const q = query(usersRef, where("email", "==", email));

      // Execute the query and get the snapshot of matching documents
      const querySnapshot = await getDocs(q);

      // Iterate through the matching documents and update the name
      const updatePromises = querySnapshot.docs.map((docSnapshot) => {
        const userDocRef = doc(usersRef, docSnapshot.id);
        return updateDoc(userDocRef, { name: updatedUsername });
      });

      await Promise.all(updatePromises);
      toast.success("Successfully updated user!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setDeleteLoading(false);
      fetchUsers();

      console.log("Name updated successfully!");
    } catch (error) {
      toast.error("Error occurred while updating user!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setDeleteLoading(false);
      console.error("Error updating name:", error);
    }
  };

  // Delete user from firebase
  const deleteUserByEmail = async (email, user) => {
    setDeleteLoading(true);

    // Create callable reference to our Cloud Function
    const deleteUserFunction = httpsCallable(functions, "deleteUser");

    try {
      // Call the function and wait for response
      const result = await deleteUserFunction({ email: email });

      // Check if there is any error
      if (result.data.error) {
        throw new Error(result.data.error);
      }

      toast.success("Successfully deleted user!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setDeleteLoading(false);
      fetchUsers();
      fetchPosts();
      console.log("User deleted successfully!");
    } catch (error) {
      toast.error("Error occurred while deleting user!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setDeleteLoading(false);
      console.error("Error deleting user document:", error);
    }

    // })
    // .catch((error) => {
    //   console.log("LOL", currentUser);

    //   console.error("Error deleting user account: ", error);
    // });
  };

  // --------------------------Posts related--------------------------------
  //Fetch posts data from firebase
  const fetchPosts = async () => {
    try {
      const temp = [];
      const q = query(collection(db, "posts"), orderBy("date_uploaded", "desc"));
      const querySnapshot = await getDocs(q);      
      querySnapshot.forEach((doc) => {
        console.log(doc.id);
        temp.push({ ...doc.data(), id: doc.id });
      });
      setPostsData(temp);
    } catch (e) {
      console.log(e);
    }
  };

  //Add new post to firebase
  const addPost = async (newPost) => {
    try {
      await addDoc(collection(db, "posts"), newPost)
        .then(() => {
          toast.success("Successfully added new post!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchPosts();
        })
        .catch((err) => {
          toast.error("Error occurred while adding post!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          console.error("Error: ", err);
        });
    } catch (error) {
      console.error("Error adding post: ", error);
      throw error;
    }
  };

  // Promote / demote a post as home slider banner
  const togglePostSlider = async (post) => {
    setDeleteLoading(true);
    try {
      const postDocRef = doc(db, "posts", post.id);
      const nextIsSlider = !post.is_slider;
      let sliderOrder = post.slider_order || 0;

      if (nextIsSlider) {
        const maxOrder = postsData
          .filter((p) => p.is_slider)
          .reduce((max, p) => Math.max(max, p.slider_order || 0), 0);
        sliderOrder = maxOrder + 1;
      } else {
        sliderOrder = 0;
      }

      await updateDoc(postDocRef, {
        is_slider: nextIsSlider,
        slider_order: sliderOrder,
      });

      toast.success(
        nextIsSlider
          ? "Post promoted to slider!"
          : "Post removed from slider!",
        {
          position: "bottom-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        }
      );
      fetchPosts();
    } catch (error) {
      toast.error("Error updating slider status!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      console.error("Error toggling slider:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Fetch post to update is_pending in the firebase
  const fetchAndUpdatePendingPost = async (id) => {
    setDeleteLoading(true);
    try {
      // Get the reference to the document in the "posts" collection
      const postDocRef = doc(db, "posts", id);

      // Iterate through the matching documents and update the name
      updateDoc(postDocRef, { is_pending: false })
        .then(() => {
          toast.success("Successfully approved post!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchPosts();
        })
        .catch((err) => {
          toast.error("Error occurred while approving post!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchPosts();
          console.error("Error: ", err);
        });

      console.log("Post Approved successfully!");
    } catch (error) {
      console.error("Error approving post:", error);
    }
  };

  // Delete post from firebase
  const deletePostById = async (id, { silent = false } = {}) => {
    setDeleteLoading(true);
    try {
      const postDocRef = doc(db, "posts", id);
      await deleteDoc(postDocRef);

      if (!silent) {
        toast.success("Successfully deleted post!", {
          position: "bottom-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      }
      setDeleteLoading(false);
    } catch (error) {
      console.error("Error deleting post:", error);
      if (!silent) {
        toast.error("Error occurred while deleting post!", {
          position: "bottom-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      }
      setDeleteLoading(false);
      throw error;
    }
  };

  // --------------------------Sliders related--------------------------------
  const fetchSliders = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "sliders"));
      const temp = querySnapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSlidersData(temp);
    } catch (e) {
      console.error("Error fetching sliders:", e);
      toast.error("Error occurred while fetching sliders!", {
        position: "bottom-left",
        autoClose: 5000,
        theme: "light",
      });
    }
  };

  const uploadSliderImage = async (file) => {
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const storageRef = ref(storage, `sliders/${Date.now()}_${safeName}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const deleteSliderStorageImage = async (imageUrl) => {
    if (!imageUrl || !imageUrl.includes("firebasestorage.googleapis.com")) {
      return;
    }
    try {
      const storageRef = ref(storage, imageUrl);
      await deleteObject(storageRef);
    } catch (e) {
      // Non-blocking: Firestore doc is still deleted even if Storage cleanup fails.
      console.warn("Could not delete slider image from Storage:", e);
    }
  };

  const saveSlider = async (payload, imageFile, existingId = null) => {
    setDeleteLoading(true);
    try {
      let imageUrl = payload.image || "";
      if (imageFile) {
        imageUrl = await uploadSliderImage(imageFile);
      }

      const action = existingId ? "update" : "create";
      const before = existingId
        ? sliderSnapshot(slidersData.find((s) => s.id === existingId))
        : null;

      const audit = await getClientEditMeta({
        section: "slider",
        action,
        targetId: existingId,
      });

      const data = {
        image: imageUrl,
        title: payload.title || "",
        link_type: payload.link_type,
        link: payload.link || "",
        order: Number(payload.order) || 0,
        is_active: Boolean(payload.is_active),
        ...audit,
      };
      // Do not persist internal geo helper on the document.
      delete data._geo;

      if (!data.image) {
        throw new Error("Image is required");
      }

      let targetId = existingId;
      if (existingId) {
        await updateDoc(doc(db, "sliders", existingId), data);
        toast.success("Successfully updated slider!", {
          position: "bottom-left",
          autoClose: 5000,
          theme: "light",
        });
      } else {
        const created = await addDoc(collection(db, "sliders"), data);
        targetId = created.id;
        toast.success("Successfully added slider!", {
          position: "bottom-left",
          autoClose: 5000,
          theme: "light",
        });
      }

      await writeFullAuditLog({
        section: "slider",
        action,
        collectionName: "sliders",
        targetId,
        before,
        after: sliderSnapshot({ ...data, id: targetId }),
        meta: { ...audit, last_edit_target_id: targetId },
        extra: { image_uploaded: Boolean(imageFile) },
      });

      await fetchSliders();
    } catch (error) {
      console.error("Error saving slider:", error);
      toast.error("Error occurred while saving slider!", {
        position: "bottom-left",
        autoClose: 5000,
        theme: "light",
      });
      throw error;
    } finally {
      setDeleteLoading(false);
    }
  };

  const deleteSliderById = async (slider) => {
    setDeleteLoading(true);
    try {
      const audit = await getClientEditMeta({
        section: "slider",
        action: "delete",
        targetId: slider.id,
      });
      await writeFullAuditLog({
        section: "slider",
        action: "delete",
        collectionName: "sliders",
        targetId: slider.id,
        before: sliderSnapshot(slider),
        after: null,
        meta: audit,
      });
      await deleteDoc(doc(db, "sliders", slider.id));
      await deleteSliderStorageImage(slider.image);
      toast.success("Successfully deleted slider!", {
        position: "bottom-left",
        autoClose: 5000,
        theme: "light",
      });
      await fetchSliders();
    } catch (error) {
      console.error("Error deleting slider:", error);
      toast.error("Error occurred while deleting slider!", {
        position: "bottom-left",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleSliderActive = async (slider) => {
    setDeleteLoading(true);
    try {
      const nextActive = !slider.is_active;
      const audit = await getClientEditMeta({
        section: "slider",
        action: "toggle_active",
        targetId: slider.id,
      });
      const stamp = { ...audit };
      delete stamp._geo;
      await updateDoc(doc(db, "sliders", slider.id), {
        is_active: nextActive,
        ...stamp,
      });
      await writeFullAuditLog({
        section: "slider",
        action: "toggle_active",
        collectionName: "sliders",
        targetId: slider.id,
        before: sliderSnapshot(slider),
        after: sliderSnapshot({ ...slider, is_active: nextActive }),
        meta: audit,
      });
      await fetchSliders();
    } catch (error) {
      console.error("Error toggling slider:", error);
      toast.error("Error occurred while updating slider!", {
        position: "bottom-left",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const moveSliderOrder = async (sliderId, direction) => {
    const sorted = [...slidersData].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    const index = sorted.findIndex((s) => s.id === sliderId);
    if (index < 0) return;

    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= sorted.length) return;

    const current = sorted[index];
    const neighbor = sorted[swapIndex];
    const currentOrder = current.order ?? index;
    const neighborOrder = neighbor.order ?? swapIndex;

    setDeleteLoading(true);
    try {
      const audit = await getClientEditMeta({
        section: "slider",
        action: `reorder_${direction}`,
        targetId: sliderId,
      });
      const stamp = { ...audit };
      delete stamp._geo;
      const batch = writeBatch(db);
      batch.update(doc(db, "sliders", current.id), {
        order: neighborOrder,
        ...stamp,
      });
      batch.update(doc(db, "sliders", neighbor.id), {
        order: currentOrder,
        ...stamp,
        last_edit_target_id: neighbor.id,
      });
      await batch.commit();
      await writeFullAuditLog({
        section: "slider",
        action: `reorder_${direction}`,
        collectionName: "sliders",
        targetId: sliderId,
        before: {
          current: sliderSnapshot(current),
          neighbor: sliderSnapshot(neighbor),
        },
        after: {
          current: sliderSnapshot({ ...current, order: neighborOrder }),
          neighbor: sliderSnapshot({ ...neighbor, order: currentOrder }),
        },
        meta: audit,
        extra: {
          swapped_with: neighbor.id,
        },
      });
      await fetchSliders();
    } catch (error) {
      console.error("Error reordering sliders:", error);
      toast.error("Error occurred while reordering sliders!", {
        position: "bottom-left",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  //----------------------Website Content related--------------------------
  // Content lives in stable admin/app_content (not tied to any Auth UID).
  const fetchWebsiteContent = async () => {
    try {
      const websiteContent = await loadOrMigrateAdminContent(db);
      if (websiteContent) {
        setWebsiteContentData(websiteContent);
      } else {
        console.log("No admin content document found.");
      }
    } catch (error) {
      console.log("Error: ", error);
    }
  };

  // Fetch and update content in the firebase
  const fetchAndUpdateWebsiteContent = async (updateData, logoData) => {
    setDeleteLoading(true);
    try {
      if (logoData.file) {
        // Upload the logo image to Firebase Storage
        const storageRef = ref(storage, `assets/logo.png`);
        await uploadBytes(storageRef, logoData.file)
          .then(() => {
            console.log("Uploaded logo successfully.");
          })
          .catch((e) => {
            console.error("Error uploading logo: ", e);
          });

        // Get the download URL of the uploaded image
        const downloadURL = await getDownloadURL(storageRef);
        setLogo(downloadURL);
      }
      const websiteContentDocRef = doc(db, "admin", ADMIN_CONTENT_DOC_ID);
      const payload = {
        ...contentFieldsOnly(updateData),
      };

      const audit = await getClientEditMeta({
        section: "website",
        action: "update",
        targetId: ADMIN_CONTENT_DOC_ID,
      });
      const stamp = { ...audit };
      delete stamp._geo;

      const before = websiteSnapshot(websiteContentData);

      await setDoc(
        websiteContentDocRef,
        {
          ...payload,
          ...stamp,
        },
        { merge: true }
      )
        .then(async () => {
          await writeFullAuditLog({
            section: "website",
            action: "update",
            collectionName: "admin",
            targetId: ADMIN_CONTENT_DOC_ID,
            before,
            after: websiteSnapshot({ id: ADMIN_CONTENT_DOC_ID, ...payload }),
            meta: audit,
            extra: {
              logo_uploaded: Boolean(logoData?.file),
            },
          });
          toast.success("Successfully updated website content!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchWebsiteContent();
        })
        .catch((err) => {
          toast.error("Error occurred while updating website content!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchWebsiteContent();
          console.error("Error fetching web data: ", err);
        });
    } catch (error) {
      console.error("Error updating website content:", error);
      setDeleteLoading(false);
    }
  };

  // --------------------------Categories related--------------------------------
  //Fetch categories data from firebase
  const fetchCategories = async () => {
    try {
      const temp = [];
      const querySnapshot = await getDocs(collection(db, "categories"));
      querySnapshot.forEach((doc) => {
        temp.push({ id: doc.id, ...doc.data() });
      });
      setCategoriesData(temp);
    } catch (e) {
      console.log("Error fetching categories: ", e);
    }
  };

  //  Add a new category to the collection
  const addCategory = async (newCategory) => {
    try {
      await addDoc(collection(db, "categories"), newCategory)
        .then(() => {
          toast.success("Successfully added new category!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchCategories();
        })
        .catch((err) => {
          toast.error("Error occurred while adding category!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchCategories();
          console.error("Error: ", err);
        });
    } catch (error) {
      console.error("Error adding category: ", error);
    }
  };

  // Fetch and update categories in the firebase
  const fetchAndUpdateCategories = async (updateData) => {
    try {
      // Get the reference to the document in the "categories" collection
      const categoryDocRef = doc(db, "categories", updateData.id);

      // Update the document fields with the provided data object
      await updateDoc(categoryDocRef, updateData)
        .then(() => {
          toast.success("Successfully updated the category!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchCategories();
        })
        .catch((err) => {
          toast.error("Error occurred while updating category!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchCategories();
          console.error("Error: ", err);
        });

      // Fetch the updated categories
      fetchCategories();
      console.log("Categories updated successfully!");
    } catch (error) {
      console.error("Error updating categories:", error);
    }
  };

  // Delete category from firebase
  const deleteCategoryById = async (id) => {
    setDeleteLoading(true);
    try {
      // Get the reference to the document in the "categories" collection
      const categoryDocRef = doc(db, "categories", id);

      // Delete the document
      await deleteDoc(categoryDocRef)
        .then(() => {
          toast.success("Successfully deleted the category!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchCategories();
        })
        .catch((err) => {
          toast.error("Error occurred while deleting category!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchCategories();
          console.error("Error: ", err);
        });

      fetchCategories();
      console.log("Category deleted successfully!");
    } catch (error) {
      console.error("Error deleting category:", error);
    }
  };

  const resetCategoriesForOrder = async (updatedData) => {
    // setDeleteLoading(true);
    try {
      // Delete the existing "categories" collection
      const categoriesCollectionRef = collection(db, "categories");
      const categoriesSnapshot = await getDocs(query(categoriesCollectionRef));
      const categoriesExist = !categoriesSnapshot.empty;

      if (categoriesExist) {
        // If the collection exists, delete all existing documents
        const batch = writeBatch(db);
        categoriesSnapshot.forEach((doc) => {
          batch.delete(doc.ref);
        });
        await batch.commit();
      }

      // Create a new collection and add each object as a document
      const batch = writeBatch(db);
      const newCategoriesCollectionRef = collection(db, "categories");
      updatedData.forEach(({ id, ...dataWithoutId }) => {
        const docRef = doc(newCategoriesCollectionRef);
        batch.set(docRef, dataWithoutId);
      });

      await batch.commit().then(() => {
        toast.success("Categories order updated successfully!", {
          position: "bottom-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        console.log("Categories updated successfully!");
        fetchCategories();
        setDeleteLoading(false);
      });

      // Fetch the updated categories
    } catch (error) {
      toast.error("Error occurred while updating category!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      fetchCategories();

      setDeleteLoading(false);
      console.error("Error: ", error);
    }
  };

  const Colors = {
    primaryColor: "#f44336",
    accentColor: "#f44336",
    accentColorLight: "#d2d2d2",
  };

  const logs = [
    { lid: 0, uid: "12", date: Date(), message: "success login to system!" },
    { lid: 1, uid: "12", date: Date(), message: "faliure login to system!" },
    { lid: 2, uid: "12", date: Date(), message: "faliure login to system!" },
    { lid: 3, uid: "12", date: Date(), message: "success login to system!" },
    { lid: 4, uid: "12", date: Date(), message: "success login to system!" },
    { lid: 5, uid: "12", date: Date(), message: "warning login to system!" },
    { lid: 6, uid: "12", date: Date(), message: "warning login to system!" },
    { lid: 7, uid: "12", date: Date(), message: "success login to system!" },
    { lid: 8, uid: "12", date: Date(), message: "fatal login to system!" },
    { lid: 9, uid: "12", date: Date(), message: "fatal login to system!" },
    { lid: 10, uid: "15", date: Date(), message: "success login to system!" },
    { lid: 11, uid: "15", date: Date(), message: "success login to system!" },
    { lid: 12, uid: "15", date: Date(), message: "faliure login to system!" },
    { lid: 13, uid: "15", date: Date(), message: "faliure login to system!" },
    { lid: 14, uid: "15", date: Date(), message: "success login to system!" },
    { lid: 15, uid: "15", date: Date(), message: "success login to system!" },
    { lid: 16, uid: "15", date: Date(), message: "warning login to system!" },
    { lid: 17, uid: "15", date: Date(), message: "warning login to system!" },
  ];

  function siRound(x) {
    if (x < 1e3) return x + "";
    const digits = Math.log10(x) | 0;
    const tier = (digits / 3) | 0;
    let str = (x / 10 ** (tier * 3)).toFixed(2 - (digits % 3));
    // Turn "10.00" into "10.0" and "100.0" into "100"
    str = str.replace(/^(.{3})\..+|^(.{4}).+/, "$1$2");
    if (str.length > 4) {
      ///Add this check
      siRound(str);
    }
    return str + ["", "k", "M", "G", "T"][tier];
  }

  return (
    <DataContext.Provider
      value={{
        logs,
        deleteLoading,
        setDeleteLoading,
        tableData,
        setTableData,
        tableLoading,
        Colors,
        fetchAndUpdateName,
        deleteUserByEmail,
        postsData,
        setPostsData,
        fetchAndUpdatePendingPost,
        togglePostSlider,
        deletePostById,
        categoriesData,
        setCategoriesData,
        addCategory,
        fetchAndUpdateCategories,
        deleteCategoryById,
        resetCategoriesForOrder,
        websiteContentData,
        fetchAndUpdateWebsiteContent,
        logo,
        addPost,
        fetchPosts,
        slidersData,
        setSlidersData,
        fetchSliders,
        saveSlider,
        deleteSliderById,
        toggleSliderActive,
        moveSliderOrder,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
export default DataProvider;
